import { useEffect, useState } from 'react';

// **********************************************************
// Utility
// **********************************************************
export const getSmallestArrayObjectValueByKey = (array, key) =>
  array.reduce(
    (acc, item) => (acc = acc < item[key] ? acc : item[key]),
    array[0][key],
  );
export const getLargestArrayObjectValueByKey = (array, key) =>
  array.reduce((acc, item) => (acc = acc > item[key] ? acc : item[key]), 0);

export const compare = (key, order = 'asc') => {
  return (a, b) => {
    if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
      // property doesn't exist on either object
      return 0;
    }

    const varA = typeof a[key] === 'string' ? a[key].toUpperCase() : a[key];
    const varB = typeof b[key] === 'string' ? b[key].toUpperCase() : b[key];

    let comparison = 0;
    if (varA > varB) {
      comparison = 1;
    } else if (varA < varB) {
      comparison = -1;
    }
    return order === 'desc' ? comparison * -1 : comparison;
  };
};

// **********************************************************
// Constants
// **********************************************************

export const fallbackDefaultPageSize = 50;
export const fallbackDefaultPageSizeOptions = [
  { key: 0, value: 10, text: 10 },
  { key: 1, value: 25, text: 25 },
  { key: 2, value: 50, text: 50 },
  { key: 3, value: 100, text: 100 },
];

// **********************************************************
// Helpers
// **********************************************************
export const getLastOptionValue = (options) => options.slice(-1)[0]?.value;

export const getSubsetOrDefaultPageSizeOptions = (totalItems, options) => {
  const smallestPageSize = getSmallestArrayObjectValueByKey(options, 'value');
  return totalItems < smallestPageSize
    ? null
    : options.filter((item) => item.value <= totalItems);
};

export const shouldAppendTotalSizeToOptionsList = (totalItems, options) => {
  if (!options) return false;
  const largestPageSize = getLargestArrayObjectValueByKey(options, 'value');
  return totalItems > largestPageSize;
};

export const getUpdatedPageSizeOptions = (baselineOptions, totalItems) => {
  const largestPageSizeKey = getLargestArrayObjectValueByKey(
    baselineOptions,
    'key',
  );
  const nextKeyValue = largestPageSizeKey + 1;
  const newOption = { key: nextKeyValue, value: totalItems, text: totalItems };
  return [...baselineOptions, newOption];
};

export const getPageSizeOptions = (totalItems, options) => {
  const baselineOptions = getSubsetOrDefaultPageSizeOptions(
    totalItems,
    options.sort(compare('key', 'asc')),
  );
  const shouldAppendTotal = shouldAppendTotalSizeToOptionsList(
    totalItems,
    baselineOptions,
  );
  return shouldAppendTotal
    ? getUpdatedPageSizeOptions(baselineOptions, totalItems)
    : baselineOptions;
};

export const getPageSize = (size, options = [], defaults) => {
  const hasNoOptions = !options?.length;
  if (hasNoOptions) return null;

  const { defaultPageSizeOptions: pageSizes, defaultPageSize } = defaults;

  const lastOptionValue = getLastOptionValue(options);
  const lastDefaultOptionValue = getLastOptionValue(pageSizes);
  const sizeAsNumber = Number(size);
  const pageSizeToReturnForLargerDatasets =
    sizeAsNumber > 0 ? sizeAsNumber : defaultPageSize;
  // const pageSizeToReturnForLargerDatasets = sizeAsNumber > 0 ? sizeAsNumber : options;
  const usingBaselineOptions = lastOptionValue === lastDefaultOptionValue;

  // console.log('getPageSize', { size, options, pageSizes, defaultPageSize, lastOptionValue, lastDefaultOptionValue, sizeAsNumber, pageSizeToReturnForLargerDatasets, usingBaselineOptions, weAppendedTotalItemCountToPageSize: options.length > pageSizes.length, weHaveLessItemsThanBaselinePageSizeCanDealWith: sizeAsNumber < lastOptionValue })

  if (usingBaselineOptions) return pageSizeToReturnForLargerDatasets;

  const weAppendedTotalItemCountToPageSize = options.length > pageSizes.length;
  if (weAppendedTotalItemCountToPageSize)
    return pageSizeToReturnForLargerDatasets;

  // Reduce options footprint as we can't page through more than what is available
  const weHaveLessItemsThanBaselinePageSizeCanDealWith =
    sizeAsNumber < lastOptionValue;
  return weHaveLessItemsThanBaselinePageSizeCanDealWith
    ? sizeAsNumber
    : lastOptionValue;
};

export const usePageSize = ({
  pageSize,
  totalItems,
  onPageSizeChanged,
  defaultPageSize = fallbackDefaultPageSize,
  defaultPageSizeOptions = fallbackDefaultPageSizeOptions,
  alwaysShowPageNumbers = true,
}) => {
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);

  const handlePageSizeChange = (e, { value }, pageSizeMatchesOptionCount) => {
    const dynamicPageNumberVisibilityValue = alwaysShowPageNumbers
      ? false
      : pageSizeMatchesOptionCount;
    onPageSizeChanged(value, dynamicPageNumberVisibilityValue);
  };

  const options = getPageSizeOptions(totalItems, defaultPageSizeOptions);

  const initialPageSize = getPageSize(pageSize, options, {
    defaultPageSize,
    defaultPageSizeOptions,
  });

  const show = options?.length;

  useEffect(() => {
    setCurrentPageSize(pageSize);
  }, [pageSize]);

  useEffect(() => {
    setCurrentPageSize(initialPageSize);
  }, [initialPageSize]);

  return { options, show, currentPageSize, handlePageSizeChange };
};

export default usePageSize;
